import { useCallback, useEffect, useMemo, useState } from "react";
import { FocusedInput, OnDatesChangeProps, START_DATE } from "@datepicker-react/hooks";
import { addDays, addMonths, differenceInDays, format, isLastDayOfMonth, parseISO, toDate } from "date-fns";
import { usePathname } from "next/navigation";
import MobileAccordianCommon from "components/searchBar/mobile-tablet-view/accordian-common/MobileAccordianCommon";
import CalenderForMobile from "components/searchBar/mobile-tablet-view/SearchCalender/CalenderForMobile";
import useCalendarCoverage from "hooks/swr/useCalendarCoverage";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { markLocalSearch, selectLocalSearchDateRange } from "stores/local-search/localSearchSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow, selectUI } from "stores/ui/uiSlice";
import { getFormattedDate } from "utils/dates";
import { isDesktop } from "utils/dom";
import { formatDateRange } from "utils/helper";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
const CalenderAccordian = ({
  isDateCleared,
  setIsDateCleared
}: {
  isDateCleared: boolean;
  setIsDateCleared: (value: boolean) => void;
}) => {
  const localSearchDates = useAppSelector(selectLocalSearchDateRange);
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const dispatch = useAppDispatch();
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const {
    data
  } = useCalendarCoverage();
  const coverage = data?.coverage ?? 180;
  const [focusedInput, setFocusedInput] = useState<FocusedInput>(null);

  // Function to change the focused input, wrapped in useCallback for performance optimization
  const onFocusChange = useCallback((focusInput: FocusedInput) => {
    setFocusedInput(focusInput);
  }, []);
  const maxBookingDate = useMemo(() => {
    return addDays(new Date(), focusedInput === "startDate" ? coverage - 1 : coverage);
  }, [coverage, focusedInput]);
  const handleSearchFlow = useCallback((newCurrentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab: newCurrentSearchTab
    }));
  }, [dispatch]);
  const onCloseCalendar = useCallback(() => {
    handleSearchFlow(null);
  }, [handleSearchFlow]);
  const onReset = useCallback(() => {
    dispatch(markLocalSearch({
      dateRangeData: {
        endDate: null,
        startDate: null,
        noOfDays: 0
      }
    }));
    onFocusChange(START_DATE);
    if (isDesktop()) {
      onCloseCalendar();
    }
  }, [dispatch, onCloseCalendar, onFocusChange]);

  // When user click on 'Clear' button on drawer footer, then this userEffect will be called.
  useEffect(() => {
    if (isDateCleared) {
      onReset();
      setIsDateCleared(false);
    }
  }, [isDateCleared, onReset]);
  const onDatesChange = useCallback((newDates: OnDatesChangeProps) => {
    if (!newDates.focusedInput && newDates.startDate === newDates.endDate) {
      return;
    }
    const startDate = newDates.startDate ? format(newDates.startDate, "yyyy-MM-dd") : null;
    const endDate = newDates.endDate ? format(newDates.endDate, "yyyy-MM-dd") : null;
    // can't select same date for booking
    if (startDate === endDate) {
      return;
    }
    dispatch(markLocalSearch({
      locationData: {
        page: 0
      },
      dateRangeData: {
        startDate,
        endDate
      }
    }));
    onFocusChange(newDates.focusedInput);
    if (startDate && endDate) {
      let noOfDays = 0;
      noOfDays = differenceInDays(parseISO(endDate), parseISO(startDate));
      dispatch(markLocalSearch({
        locationData: {
          page: 0
        },
        dateRangeData: {
          noOfDays: noOfDays || 0
        }
      }));
    }
  }, [dispatch, onFocusChange]);
  useEffect(() => {
    if (currentSearchTab === "dates" && focusedInput === null) {
      onFocusChange(START_DATE);
    }
  }, [focusedInput, onFocusChange, currentSearchTab]);
  const {
    searchStartDate,
    searchEndDate
  } = getFormattedDate(localSearchDates?.startDate || null, localSearchDates?.endDate || null);
  // in some cases, we get startDate & endDate are 'null'/null
  // we use this conditions to provide valid date to format function
  const dateRange = formatDateRange(searchStartDate, searchEndDate, false);
  return <MobileAccordianCommon currentTab='dates' title='When will you be there?' className='max-h-[500px]' additionalSummaryContent={<p className='text-sm font-semibold leading-[14px] tracking-[-0.14px] text-[#26262B]'>
          {searchStartDate !== "" && searchEndDate !== "" ? dateRange : "Anytime"}
        </p>} data-sentry-element="MobileAccordianCommon" data-sentry-component="CalenderAccordian" data-sentry-source-file="CalenderAccordian.tsx">
      {/* Calender for mobile & table view */}
      <div className={`calender-mobile-scrollbar overflow-x-hidden overflow-y-scroll px-0 pb-4 ssxm:px-2 sm:px-4 ${isHomePage ? "max-h-[min(calc(100dvh-400px),400px)]" : "max-h-[400px] min-h-[300px]"}`}>
        <CalenderForMobile onClose={onCloseCalendar} onDatesChange={onDatesChange} onReset={onReset} vertical={!isLargerThan992} maxBookingDate={maxBookingDate} minBookingDate={addDays(new Date(), 1)} startDate={localSearchDates?.startDate && toDate(parseISO(localSearchDates?.startDate)) || null} endDate={localSearchDates?.endDate && toDate(parseISO(localSearchDates?.endDate)) || null} focusedInput={focusedInput} initialVisibleMonth={isLastDayOfMonth(new Date()) ? addMonths(new Date(), 1) : new Date()} data-sentry-element="CalenderForMobile" data-sentry-source-file="CalenderAccordian.tsx" />
      </div>
    </MobileAccordianCommon>;
};
export default CalenderAccordian;